<template>
  <div id="templates-list-v2" class="container item-list pb-64">
    <v-list-header :title="$t('template.title')" icon="compass"
      @open-filters-offcanvas="openOffCanvas('the-template-filters-offcanvas')" @emit-close-off-canvas="closeOffCanvas"
      @reload-client-tours="loadTemplates">
      <template #input-search>
        <v-input-text-search id="templates-search" v-model="name" :placeholder="$t('general.shared.search')"
          autocomplete="off" class="w-100 me-24" input-class="h-auto py-8" input-group-text-class="py-8 px-16"
          type="text" @update:modelValue="
            name = $event;
          search();
          " />
      </template>
    </v-list-header>

    <div class="items-content">
      <div class="filters-container mb-24">
        <v-filters v-if="!!filterOptions" v-model="filterOptions" @remove-filter="removeFilter" />
      </div>

      <div class="table-responsive d-none d-md-flex">
        <v-table>
          <v-table-header>
            <tr>
              <th>
                <v-button :class="{ 'sort-active': order.key === 'name' }"
                  class="btn-order text-md fw-light shadow-none p-0 pb-4" variant="icon" @click="setOrder('name')">
                  {{ $t("general.shared.name") }}
                  <v-icon :class="{ 'sort-icon-transition': order.key === 'name' && order.order === 'DESC' }"
                    class="ms-8" icon="chevron-down" size="xs" />
                </v-button>
              </th>

              <th>
                <v-button :class="{ 'sort-active': order.key === 'travel_type' }"
                  class="btn-order text-md fw-light shadow-none p-0 pb-4 text-nowrap" variant="icon"
                  @click="setOrder('travel_type')">
                  {{ $t("template.travelType") }}
                  <v-icon :class="{ 'sort-icon-transition': order.key === 'travel_type' && order.order === 'DESC' }"
                    class="ms-8" icon="chevron-down" size="xs" />
                </v-button>
              </th>

              <th>{{ $t("general.shared.origin") }}</th>
              <th>{{ $t("general.shared.destination") }}</th>
              <th class="fit" />
            </tr>
          </v-table-header>

          <v-table-body class="fw-medium">
            <tr v-if="!loaded">
              <td class="position-relative border-0 py-96" colspan="2222">
                <v-loader />
              </td>
            </tr>

            <template v-else-if="!!templates && templates.length > 0">
              <tr v-for="item in templates" :key="item.id">
                <td class="d-flex">
                  <div class="image-container rounded-sm bg-tertiary me-12">
                    {{ item.name?.charAt(0) }}
                  </div>

                  <router-link v-if="!authStore?.agency?.isCollaborator && !item.deletedAt" :aria-label="item.name" :to="{
                    name: 'activities.show',
                    params: {
                      activitiesId: item.id,
                    },
                  }" class="py-12">
                    {{ item.name }}
                    <span class="d-none d-md-inline" v-if="item.isCollaborator">
                      ({{ $t("general.shared.collaborator") }})</span>
                  </router-link>

                  <a v-else :aria-label="item.name" class="py-12" href="#" @click.prevent="setCurrentTemplate(item)">
                    {{ item.name }}
                  </a>
                </td>

                <td>{{ findConceptByKey("tour.travel_type", item.travelType).name }}</td>
                <td>{{ item.origin?.name }}</td>
                <td>{{ item.destination?.name }}</td>

                <td class="fit text-end">
                  <!-- Preview Button -->
                  <v-button class="btn-view rounded-pill bg-primary ms-8" size="xs" variant="icon"
                    :aria-label="$t('template.previewTitle')" @click="openPreviewModal(item)"
                    style="margin-right: 5px;">
                    <v-icon icon="eye" />
                  </v-button>
                  <router-link v-if="!authStore?.agency?.isCollaborator && !item.deletedAt"
                    :aria-label="$t('template.show')" :to="{
                      name: 'activities.show',
                      params: {
                        activitiesId: item.id,
                      },
                    }" class="btn btn-icon btn-view rounded-pill bg-secondary">
                    <v-icon icon="arrow-right" />
                  </router-link>

                  <v-button v-else class="btn-view rounded-pill bg-secondary" size="xs" variant="icon"
                    @click="setCurrentTemplate(item)">
                    <v-icon icon="arrow-right" />
                  </v-button>
                </td>
              </tr>
            </template>

            <template v-else>
              <tr>
                <td class="rounded-xs py-16" colspan="2222">
                  {{ $t("template.noProducts") }}
                </td>
              </tr>
            </template>
          </v-table-body>
        </v-table>
      </div>

      <div class="item-cards d-block d-md-none">
        <div v-if="!loaded" class="position-relative py-96">
          <v-loader />
        </div>

        <template v-else-if="!!templates && templates.length > 0">
          <div v-for="item in templates" :key="item.id" class="item-card border rounded-xs p-8 mb-12 text-sm">
            <div class="d-flex">
              <div class="image-container rounded-sm bg-tertiary me-12">
                {{ item.name?.charAt(0) }}
              </div>

              <div class="w-100">
                <router-link :disabled="authStore?.agency?.isCollaborator" v-if="!item.deletedAt"
                  :aria-label="item.name" :to="{
                    name: 'activities.show',
                    params: {
                      activitiesId: item.id,
                    },
                  }" class="fw-medium">
                  {{ item.name }}
                </router-link>

                <div class="text-xs fw-light mt-4">
                  <template v-if="!!item.origin || !item.destination">
                    {{ !!item.origin ? item.origin.name : "-" }}

                    <v-icon icon="arrow-right" size="xs" space="px-8" />

                    {{ !!item.destination ? item.destination.name : "-" }}
                  </template>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-between mt-12 align-items-center">
              <div class="fw-light text-xs">
                {{ findConceptByKey("tour.travel_type", item.travelType).name }}
              </div>

              <router-link v-if="!item.deletedAt" :aria-label="$t('template.show')" :to="{
                name: 'activities.show',
                params: {
                  activitiesId: item.id,
                },
              }" class="btn btn-icon btn-view rounded-pill bg-secondary">
                <v-icon icon="arrow-right" size="xs" />
              </router-link>

              <v-button v-else class="btn-view rounded-pill bg-secondary" size="xs" variant="icon"
                @click="setCurrentTemplate(item)">
                <v-icon icon="arrow-right" />
              </v-button>
            </div>
          </div>
        </template>

        <div v-else>
          {{ $t("template.noProducts") }}
        </div>
      </div>

      <v-pagination v-if="!!templates && templates.length > 0" :meta="pagination.meta"
        :pagination-links="pagination.links" class="mt-32" @page-changed="
          changePage($event);
        handlePageChangedEvent($event);
        " @per-page-changed="
          perPage = $event;
        doSearch();
        " />
    </div>

    <the-template-filters-offcanvas v-if="offCanvasToShow === 'the-template-filters-offcanvas'"
      v-model:destination="destination" v-model:location="location" v-model:origin="origin" v-model:status="status"
      v-model:travelType="travelType" @closed="closeOffCanvas" @selected="doSearch" />

    <the-template-archived-offcanvas v-if="offCanvasToShow === 'the-template-archived-offcanvas'"
      :template="currentTemplate" @closed="
        closeOffCanvas();
      cleanCurrentTemplate();
      " @reload-templates="loadTemplates" />

    <the-template-preview-modal v-if="previewModalVisible && previewTemplate" :agency="authStore.agency.slug"
      :activity-id="previewTemplate.id" @closed="closePreviewModal" />
  </div>
</template>

<script lang="ts" setup>
import { onMounted } from "vue";
import { VInputTextSearch } from "@uniqoders/form";
import qs from "qs";
import querystring from "query-string";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VTable from "@/components/vendor/basic/table/VTable.vue";
import VTableHeader from "@/components/vendor/basic/table/VTableHeader.vue";
import VTableBody from "@/components/vendor/basic/table/VTableBody.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import api from "@/api";
import withTimeout from "@/helpers/timeOut";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import VListHeader from "@/components/shared/VListHeader.vue";
import VPagination from "@/components/vendor/basic/table/VPagination.vue";
import usePagination from "@/helpers/pagination";
import VFilters from "@/components/vendor/filters/VFilters.vue";
import { useAuthStore } from "@/stores/auth";
import Template from "@/api/objects/Template";
import { findConceptByKey } from "@/helpers/ConceptHelper";
import TheTemplateArchivedOffcanvas from "@/components/template/TheTemplateArchivedOffcanvas.vue";
import TheTemplateFiltersOffcanvas from "@/components/template/TheTemplateFiltersOffcanvas.vue";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import TheTemplatePreviewModal from '@/components/template/TheTemplatePreviewModal.vue';

const templates = ref<Template[]>([]);
const status = ref<string>("active");
const name = ref<string>("");
const destination = ref<string>("");
const location = ref<string>("");
const origin = ref<string>("");
const travelType = ref<string>("");
const filters = ref<Record<string, string | boolean>[]>([
  {
    variable: "status",
    key: "status",
    labelAux: "general.shared.filterStatus",
    conceptName: "app.states",
    multiple: false,
    isConcept: true,
  },
  {
    variable: "name",
    key: "name",
    labelAux: "general.shared.filterSearch",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "destination",
    key: "destination",
    labelAux: "general.shared.filterDestination",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "location",
    key: "location",
    labelAux: "general.shared.filterLocation",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "origin",
    key: "origin",
    labelAux: "general.shared.filterOrigin",
    multiple: false,
    isConcept: false,
  },
  {
    variable: "travelType",
    key: "travel_type",
    labelAux: "general.shared.filterTravelType",
    conceptName: "tour.travel_type",
    multiple: false,
    isConcept: true,
  },
]);
const order = ref<Record<string, string>>({
  key: "",
  order: "",
});
const perPage = ref<number>(15);
const page = ref<number>(1);
const timers = ref<{ name: any }>({ name: null });
const loaded = ref<boolean>(false);
const currentTemplate = ref<Template | null>(null);
const filterOptions = ref<Record<string, any>[]>([]);
const previewModalVisible = ref(false);
const previewTemplate = ref<Template | null>(null);

const authStore = useAuthStore();
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();
const { pagination, changePage, setResponse } = usePagination();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

onMounted(async () => {
  await setQueryParametersAsFilters();
  await doSearch();
});

const search = () => {
  clearTimeout(timers.value.name);

  timers.value.name = withTimeout(
    async () => {
      await doSearch();
    },
    timers.value.name,
    1000
  );
};

const setQueryParametersAsFilters = async () => {
  const query = route.query;

  const parsedQuery = qs.parse(query as any);

  if (parsedQuery.filters) {
    filters.value.forEach((filter) => {
      if (parsedQuery.filters[filter.key]) {
        (filter as any).variable = parsedQuery.filters[filter.key];
      }
    });
  }

  if (parsedQuery.order) {
    order.value = parsedQuery.order;
  }

  const pageNumber = parseInt(parsedQuery.page, 10);
  if (!Number.isNaN(pageNumber)) {
    page.value = pageNumber;
  }

  const perPageNumber = parseInt(parsedQuery.perPage, 10);
  if (!Number.isNaN(perPageNumber)) {
    perPage.value = perPageNumber;
  }

  if (parsedQuery.status) {
    status.value = parsedQuery.status;
  }
};

const setFiltersAsQueryParameters = async () => {
  const filtersObject: Record<string, any> = {};
  const filterValues = {
    status,
    name,
    destination,
    location,
    origin,
    travelType,
  };

  filters.value.forEach((filter) => {
    const variableRef = filterValues[filter.variable]; // Dynamically access the correct ref
    if (variableRef?.value) {
      filtersObject[filter.key] = variableRef.value;
    }
  });

  const queryObject = {
    filters: filtersObject,
    perPage: perPage.value,
    status: status.value,
    page: page.value,
  };

  if (order.value?.key) {
    queryObject.order = order.value;
  }

  await setFilters(); // Ensure this function is properly defined

  let query = qs.stringify(queryObject, { encode: false });
  query = querystring.parse(query);

  router.push({
    name: "activities.index",
    query,
  });
};

const doSearch = async () => {
  await setFiltersAsQueryParameters();
  await loadTemplates();
};

const loadTemplates = async (pageNumber?: number) => {
  try {
    loaded.value = false;

    const filtersObj: Record<string, any> = {};

    const filterValues = {
      status,
      name,
      destination,
      location,
      origin,
      travelType,
    };

    filters.value.forEach((filter) => {
      const variableRef = filterValues[filter.variable]; // Dynamically access the correct ref
      if (variableRef?.value) {
        filtersObj[filter.key] = variableRef.value;
      }
    });

    const query: any = {
      filters: filtersObj,
      order: order.value,
      perPage: perPage.value,
      status: status.value,
      page: pageNumber ?? page.value, // If pageNumber is provided, use it; otherwise, use page.value
    };

    if (authStore.agency) {
      const response = await api.activityService.allPaginated(authStore.agency.slug, query);
      setResponse(response);
      templates.value = response.data;
    }
  } catch (e: any) {
    console.error(e);
    // Assuming toast notification is available
    // If using Vue Toastification, import it and use toast.error()
  } finally {
    loaded.value = true;
  }
};

const cleanCurrentTemplate = () => {
  currentTemplate.value = null;
};

const handlePageChangedEvent = async (page: number) => {
  await loadTemplates(page);
};

const setOrder = (field: string) => {
  if (!order.value.order) {
    order.value.order = "ASC";
  } else {
    order.value.order = order.value.order === "ASC" ? "DESC" : "ASC";
  }
  order.value.key = field;

  search();
};

const setFilters = () => {
  const filterValues = {
    status,
    name,
    destination,
    location,
    origin,
    travelType,
  };

  filterOptions.value = filters.value.flatMap((filter) => {
    const { key, multiple, isConcept, labelAux, conceptName, variable } = filter;
    const variableRef = filterValues[variable];

    if (!variableRef) {
      console.warn(`Undefined variableRef for: ${variable}`);
      return [];
    }

    const options = multiple ? variableRef.value : [variableRef.value];

    return options.map((option) => {
      const value = isConcept ? findConceptByKey(`${conceptName}`, option) : option;
      return {
        key,
        value: isConcept ? value.key : value,
        label: t(`${labelAux}`, { value: isConcept ? value.name : value }),
      };
    });
  });
};

const removeFilter = (filter) => {
  const filterObj = filters.value.find((f) => f.key === filter.key);
  if (filterObj) {
    const variableRef = eval(filterObj.variable);
    if (!filterObj.multiple) {
      variableRef.value = "";
    } else {
      variableRef.value = variableRef.value.filter((option) => option !== filter.value);
    }
  }

  if (filter.key === "status") {
    status.value = "active";
  }

  doSearch();
};

const setCurrentTemplate = (template) => {
  currentTemplate.value = template;

  if (offCanvasToShow.value !== "the-template-archived-offcanvas") {
    openOffCanvas("the-template-archived-offcanvas");
  }
};

function openPreviewModal(template: Template) {
  previewTemplate.value = template;
  previewModalVisible.value = true;
}

function closePreviewModal() {
  previewModalVisible.value = false;
  previewTemplate.value = null;
}

onUnmounted(() => {
  cleanCurrentTemplate();
});
</script>
